import { useCallback, useEffect, useState } from "react";
import { Navigate, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Loader2, UserPlus, UserX, UserCheck, Users } from "lucide-react";
import { portalApi } from "./api";

const emptyDraft = { name: "", email: "", designation: "", phone: "", password: "" };
const inputCls = "w-full rounded-lg border border-emerald-950/15 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-600";

function PasswordOnce({ password, forName }) {
  return (
    <div className="mt-4 rounded-2xl border-2 border-amber-400/60 bg-amber-50 p-4" data-testid="team-password-once">
      <p className="text-sm font-bold text-amber-900">Password for {forName}, shown once only:</p>
      <p className="mt-1 select-all rounded-lg bg-white px-3 py-2 font-mono text-sm font-bold text-forest-deep" data-testid="team-password-once-value">{password}</p>
      <p className="mt-2 text-xs text-amber-800">Copy it now and pass it to them directly. It cannot be retrieved later. If lost, set a new one.</p>
    </div>
  );
}

export default function PortalTeam() {
  const { me, reload } = useOutletContext();
  const [data, setData] = useState({ items: [], seats: { used: 0, limit: 0 } });
  const [draft, setDraft] = useState(emptyDraft);
  const [busy, setBusy] = useState(false);
  const [seatError, setSeatError] = useState("");
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeSeats, setUpgradeSeats] = useState("");
  const [upgradeNote, setUpgradeNote] = useState("");
  const [passwordOnce, setPasswordOnce] = useState(null);
  const [setPwFor, setSetPwFor] = useState(null);
  const [setPwValue, setSetPwValue] = useState("");

  const isAdmin = me.user.account_type === "client_admin";

  const load = useCallback(() => {
    portalApi("/client/users").then(setData).catch((e) => toast.error(e.message));
  }, []);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  if (!isAdmin) return <Navigate to="/portal" replace />;

  const addMember = async (e) => {
    e.preventDefault();
    setBusy(true);
    setSeatError("");
    try {
      await portalApi("/client/users", { method: "POST", body: JSON.stringify(draft) });
      setPasswordOnce({ password: draft.password, forName: draft.name });
      setDraft(emptyDraft);
      setShowUpgrade(false);
      load();
      reload();
      toast.success("Member added");
    } catch (err) {
      if (err.status === 409 && err.message.includes("Seat limit")) {
        setSeatError(err.message);
        setShowUpgrade(true);
      } else {
        toast.error(err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const submitUpgrade = async (e) => {
    e.preventDefault();
    try {
      await portalApi("/client/seat-upgrade-request", {
        method: "POST",
        body: JSON.stringify({ requested_seats: Number(upgradeSeats), note: upgradeNote }),
      });
      toast.success("Upgrade request sent. Clymaco will contact you.");
      setShowUpgrade(false);
      setUpgradeSeats("");
      setUpgradeNote("");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const savePassword = async (userId) => {
    try {
      await portalApi(`/client/users/${userId}/set-password`, { method: "POST", body: JSON.stringify({ password: setPwValue }) });
      const target = data.items.find((u) => u.id === userId);
      setPasswordOnce({ password: setPwValue, forName: target ? target.name : "member" });
      setSetPwFor(null);
      setSetPwValue("");
      toast.success("Password updated");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const disable = async (userId) => {
    try {
      await portalApi(`/client/users/${userId}/disable`, { method: "POST" });
      load();
      reload();
      toast.success("Member disabled, seat freed");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const enable = async (userId) => {
    try {
      await portalApi(`/client/users/${userId}`, { method: "PATCH", body: JSON.stringify({ status: "active" }) });
      load();
      reload();
      toast.success("Member re-enabled");
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6" data-testid="portal-team">
      <div className="flex flex-wrap items-center gap-3">
        <Users className="h-5 w-5 text-emerald-700" />
        <h1 className="font-display text-2xl font-bold text-forest-deep">Team</h1>
        <span className="rounded-full bg-emerald-900/5 px-3 py-1 font-mono text-[11px] font-semibold uppercase tracking-widest text-emerald-800/70" data-testid="team-seats">
          {data.seats.used} of {data.seats.limit} seats used
        </span>
      </div>
      <p className="mt-1 text-sm text-forest-deep/60">Members share this organisation. New regimes or streams never need new accounts.</p>

      <div className="mt-6 overflow-hidden rounded-2xl border border-emerald-950/10 bg-white shadow-sm">
        <div className="grid grid-cols-12 gap-2 border-b border-emerald-950/10 bg-emerald-900/[0.03] px-4 py-3 font-mono text-[10px] font-semibold uppercase tracking-widest text-forest-deep/50">
          <span className="col-span-3">Name</span>
          <span className="col-span-3">Email</span>
          <span className="col-span-2">Designation</span>
          <span className="col-span-2">Status</span>
          <span className="col-span-2 text-right">Actions</span>
        </div>
        {data.items.map((u) => (
          <div key={u.id} className="grid grid-cols-12 items-center gap-2 border-b border-emerald-950/5 px-4 py-3 last:border-0" data-testid={`team-row-${u.id}`}>
            <div className="col-span-3">
              <div className="text-sm font-bold text-forest-deep">{u.name}</div>
              <div className="text-[11px] font-medium uppercase tracking-wide text-emerald-800/60">{u.account_type === "client_admin" ? "Client admin" : "Member"}</div>
            </div>
            <div className="col-span-3 truncate text-sm text-forest-deep/75">{u.email}</div>
            <div className="col-span-2 text-sm text-forest-deep/60">{u.designation || "-"}</div>
            <div className="col-span-2">
              <span className={`rounded-full px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest ${u.status === "active" ? "bg-emerald-500/15 text-emerald-700" : "bg-red-500/10 text-red-700"}`} data-testid={`team-status-${u.id}`}>
                {u.status}
              </span>
              {u.last_login_at && <div className="mt-1 text-[10px] text-forest-deep/45">Last login {new Date(u.last_login_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</div>}
            </div>
            <div className="col-span-2 flex items-center justify-end gap-1.5">
              {u.account_type === "member" && (
                <>
                  <button onClick={() => { setSetPwFor(setPwFor === u.id ? null : u.id); setSetPwValue(""); }} aria-label="Set password" className="rounded-full border border-emerald-950/15 p-2 text-forest-deep transition-colors hover:bg-emerald-900/5" data-testid={`team-setpw-${u.id}`}>
                    <KeyRound className="h-3.5 w-3.5" />
                  </button>
                  {u.status === "active" ? (
                    <button onClick={() => disable(u.id)} aria-label="Disable" className="rounded-full border border-red-200 p-2 text-red-600 transition-colors hover:bg-red-50" data-testid={`team-disable-${u.id}`}>
                      <UserX className="h-3.5 w-3.5" />
                    </button>
                  ) : (
                    <button onClick={() => enable(u.id)} aria-label="Re-enable" className="rounded-full border border-emerald-600/30 p-2 text-emerald-700 transition-colors hover:bg-emerald-50" data-testid={`team-enable-${u.id}`}>
                      <UserCheck className="h-3.5 w-3.5" />
                    </button>
                  )}
                </>
              )}
            </div>
            {setPwFor === u.id && (
              <div className="col-span-12 flex flex-wrap items-center gap-2 rounded-xl bg-parchment-base p-3">
                <input type="text" minLength={10} placeholder="New password, minimum 10 characters" value={setPwValue} onChange={(e) => setSetPwValue(e.target.value)} className={`${inputCls} max-w-xs`} data-testid={`team-setpw-input-${u.id}`} />
                <button onClick={() => savePassword(u.id)} className="rounded-full bg-forest-primary px-4 py-2 text-sm font-bold text-white hover:bg-forest-rich" data-testid={`team-setpw-save-${u.id}`}>Set password</button>
              </div>
            )}
          </div>
        ))}
      </div>

      {passwordOnce && <PasswordOnce password={passwordOnce.password} forName={passwordOnce.forName} />}

      <form onSubmit={addMember} className="mt-8 rounded-2xl border-2 border-dashed border-emerald-950/15 bg-white/60 p-5" data-testid="team-add-form">
        <div className="flex items-center gap-2">
          <UserPlus className="h-4 w-4 text-emerald-700" />
          <h2 className="font-display text-base font-bold text-forest-deep">Add a member</h2>
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <input required placeholder="Full name" value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} className={inputCls} data-testid="team-add-name" />
          <input required type="email" placeholder="Work email" value={draft.email} onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))} className={inputCls} data-testid="team-add-email" />
          <input placeholder="Designation" value={draft.designation} onChange={(e) => setDraft((d) => ({ ...d, designation: e.target.value }))} className={inputCls} data-testid="team-add-designation" />
          <input placeholder="Phone (optional)" value={draft.phone} onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))} className={inputCls} data-testid="team-add-phone" />
          <input required type="text" minLength={10} placeholder="Password, minimum 10 characters" value={draft.password} onChange={(e) => setDraft((d) => ({ ...d, password: e.target.value }))} className={inputCls} data-testid="team-add-password" />
          <button type="submit" disabled={busy} className="flex items-center justify-center gap-2 rounded-full bg-forest-primary px-5 py-2.5 font-display text-sm font-bold text-white transition-colors hover:bg-forest-rich disabled:opacity-60" data-testid="team-add-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Add member
          </button>
        </div>
        {seatError && (
          <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800" data-testid="team-seat-error">{seatError}</p>
        )}
      </form>

      {showUpgrade && (
        <form onSubmit={submitUpgrade} className="mt-4 rounded-2xl border border-emerald-950/10 bg-white p-5 shadow-sm" data-testid="team-upgrade-panel">
          <h2 className="font-display text-base font-bold text-forest-deep">Need more seats?</h2>
          <p className="mt-1 text-sm text-forest-deep/60">Tell us how many and Clymaco staff will raise your limit. No payment is taken here.</p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <input required type="number" min={data.seats.limit + 1} placeholder={`Seats needed (current ${data.seats.limit})`} value={upgradeSeats} onChange={(e) => setUpgradeSeats(e.target.value)} className={`${inputCls} max-w-[220px]`} data-testid="team-upgrade-seats" />
            <input placeholder="Note (optional)" value={upgradeNote} onChange={(e) => setUpgradeNote(e.target.value)} className={`${inputCls} flex-1 min-w-[200px]`} data-testid="team-upgrade-note" />
            <button type="submit" className="rounded-full bg-forest-primary px-5 py-2.5 font-display text-sm font-bold text-white hover:bg-forest-rich" data-testid="team-upgrade-submit">
              Request seats
            </button>
          </div>
        </form>
      )}
    </main>
  );
}
